import { champFields, extractEntity } from './tournamentsApi';
import { toIsoDate } from './schedulerApi';
import { normalizeAdminMatch } from './matchesApi';

export const MATCH_REPORT_FLAGS = [
  { value: 'all', labelKey: 'rpFlagAll' },
  { value: 'no_result', labelKey: 'rpFlagNoResult' },
  { value: 'no_video', labelKey: 'rpFlagNoVideo' },
  { value: 'no_photo', labelKey: 'rpFlagNoPhoto' },
  { value: 'no_lineup', labelKey: 'rpFlagNoLineup' },
];

export const GENDER_OPTIONS = [
  { value: 'all', labelKey: 'rpGenderAll' },
  { value: 'male', labelKey: 'rpGenderMale' },
  { value: 'female', labelKey: 'rpGenderFemale' },
];

export const STATUS_OPTIONS = [
  { value: 'all', labelKey: 'rpStatusAll' },
  { value: 'amateur', labelKey: 'rpStatusAmateur' },
  { value: 'professional', labelKey: 'rpStatusPro' },
];

export const parseAgeInput = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return null;
  if (!/^\d{1,3}$/.test(raw)) return false;
  const age = Number(raw);
  if (!Number.isFinite(age) || age < 0 || age > 120) return false;
  return age;
};

const unwrapPayload = (data) => {
  if (!data || typeof data !== 'object') return {};
  if (data.data && typeof data.data === 'object' && !Array.isArray(data.data) && (data.status != null || data.config || data.headers)) {
    return extractEntity(data.data);
  }
  return extractEntity(data);
};

export const defaultDateRange = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return {
    from: `${year}-${month}-01`,
    to: `${year}-${month}-${day}`,
  };
};

export const todayIso = () => toIsoDate(new Date());

export const datesPayload = (from, to) => ({
  date_from: from,
  from,
  date_to: to,
  to,
});

export const champDatesPayload = (champId, from, to) => ({
  ...champFields(champId),
  ...datesPayload(from, to),
});

export const datesAreValid = (from, to) => Boolean(from && to && from <= to);

const asList = (value) => (Array.isArray(value) ? value : []);
const asCount = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

export const extractSummary = (data) => {
  const raw = unwrapPayload(data);
  return {
    matches_count: asCount(raw.matches_count),
    stadiums: asList(raw.stadiums),
    leagues: asList(raw.leagues),
  };
};

export const extractMatchesReport = (data) => {
  const raw = unwrapPayload(data);
  return {
    matches_count: asCount(raw.matches_count),
    leagues: asList(raw.leagues).map((league) => ({
      ...league,
      league_id: league.league_id ?? league.id,
      league_name: league.league_name || league.name || '',
      matches_count: asCount(league.matches_count),
      // date/time в отчёте — UTC naive, как в matches API
      matches: asList(league.matches).map(normalizeAdminMatch).filter((item) => item.id),
    })),
  };
};

export const extractStaffReport = (data) => {
  const raw = unwrapPayload(data);
  return asList(raw.staff).map((person) => ({
    ...person,
    id: person.id ?? person.user_id,
    matches_count: asCount(person.matches_count),
    positions: asList(person.positions),
    slots: asList(person.slots),
  }));
};

export const extractPlayersCount = (data) => {
  const raw = unwrapPayload(data);
  return {
    players_count: asCount(raw.players_count),
    cities: asList(raw.cities).map((city) => ({
      ...city,
      players_count: asCount(city.players_count),
      championships: asList(city.championships),
    })),
  };
};

export const extractPlayersList = (data) => {
  const raw = unwrapPayload(data);
  return asList(raw.players).map((player) => {
    const insured = player.isInsured === true || player.isInsured === 1 || player.isInsured === '1';
    return {
      ...player,
      isInsured: insured,
      insuranceUntil: insured ? (player.insuranceUntil || player.insurance_until || null) : null,
      teams: asList(player.teams),
    };
  });
};

export const extractBirthdays = (data) => {
  const raw = unwrapPayload(data);
  return asList(raw.players).map((player) => ({
    ...player,
    games: asCount(player.games),
    teams: asList(player.teams),
  }));
};

export const extractStats = (data) => {
  const raw = unwrapPayload(data);
  return {
    scorers: asList(raw.scorers),
    assists: asList(raw.assists),
    goals_assists: asList(raw.goals_assists),
    yellow_cards: asList(raw.yellow_cards),
    red_cards: asList(raw.red_cards),
    teams: asList(raw.teams),
    biggest_wins: asList(raw.biggest_wins).map(normalizeAdminMatch).filter((item) => item.id),
    roughest_matches: asList(raw.roughest_matches).map(normalizeAdminMatch).filter((item) => item.id),
  };
};

export const personName = (item) => {
  const family = String(item?.family || '').trim();
  const name = String(item?.name || '').trim();
  return [family, name].filter(Boolean).join(' ') || item?.login || '';
};

export const teamNames = (teams) => {
  if (!Array.isArray(teams)) return '';
  return teams
    .map((team) => (typeof team === 'string' ? team : team?.name || team?.short_name || ''))
    .filter(Boolean)
    .join(', ');
};
